import axiosInstance, { setAccessToken } from "../../axios/index";
import { alertSuccess } from "../../utils/feedback";
import {
  LOGIN,
  LOGOUT,
  CHECK_ORGA_PROFILE,
  GET_PROFILE,
  AUTOMATIC_LOGOUT,
  AUTOMATIC_LOGOUT_CLOSE,
  GOOGLE_SIGNUP,
  SET_AUTH_DIALOG,
} from "../types/userTypes";
import {
  requestFailed,
  requestStarted,
  requestSucceeded,
  loggedOut,
} from "./feedbackActionCreators";
import {
  setOrgaProfile,
  resetState,
  setOrganizationId,
} from "./organizationProfileCreators";
import { socketDisconnect } from "./notificationsActionCreator";
import axios from "axios";
import { clearRiskAssessmentStorage } from "../../utils/storageCleanup";
// import {socket} from "../actions/chatbotAction"
// import {disconnectEvent} from "../actions/chatbotAction"

export const login = (token, organization_id, redirectUrl) => ({
  type: LOGIN,
  payload: { token, organization_id, redirectUrl },
});
export const checkOrgaProfile = (orgaId) => ({
  type: CHECK_ORGA_PROFILE,
  payload: { orgaId },
});

export const disconnect = () => ({
  type: LOGOUT,
});

export const automaticLogout = () => ({
  type: AUTOMATIC_LOGOUT,
});

export const automaticLogoutClose = () => ({
  type: AUTOMATIC_LOGOUT_CLOSE,
});

export const getProfile = (profile) => ({
  type: GET_PROFILE,
  payload: profile,
});

export const setGoogleSignUp = (profile) => ({
  type: GOOGLE_SIGNUP,
  payload: profile,
});

export const setAuthDialog = (bol) => ({
  type: SET_AUTH_DIALOG,
  payload: bol,
});

// Opens the sign-out modal. The only thing that dispatches it now is the
// interceptor's terminal path, by way of the onSessionEnded subscription in
// AutomaticLogOutControl - the four action files that used to call it on any
// "token expired" were firing on the retryable 401 the interceptor refreshes and
// replays, i.e. on a session that was not over (F-24).
export const automaticSignOut = () => {
  return async (dispatch) => {
    dispatch(automaticLogout());
    clearRiskAssessmentStorage();
    dispatch(requestFailed("automaticLogout"));
  };
};

let logoutInFlight = null;

const clearLocalSession = (dispatch) => {
  dispatch(requestSucceeded());
  dispatch(loggedOut());
  dispatch(disconnect());
  dispatch(automaticLogoutClose());
  dispatch(setOrgaProfile({}, false, null, null, {}));
  dispatch(resetState());
  clearRiskAssessmentStorage();
  localStorage.removeItem("user");
  localStorage.removeItem("orgaId");
  // Nothing writes this key any more - Google's refresh token never reaches the
  // browser (F-09). The removal stays to scrub the value a previous build left
  // behind in a browser that still has one.
  localStorage.removeItem("refreshToken");
};

// Everything that has to stop being true in this tab, in the order it has to stop
// being true in: the token first, so nothing started after this point can carry
// it; the socket next, because that token is what it authenticates with; the
// store last. Shared with the password-change paths, which arrive at the same end
// state by a different route (F-43).
const endSessionLocally = async (dispatch) => {
  setAccessToken(null);
  await socketDisconnect();
  clearLocalSession(dispatch);
};

const redirectAfterLogout = (history, data) => {
  if (!history) return;

  // A second dispatch must not stack a second /login entry on the history (F-26).
  if (history.location?.pathname !== "/login") {
    history.push("/login");
  }

  if (data?.acceptInvit) {
    alertSuccess({
      message:
        "You just accepted the invitation. You have to reconnect to the application to access as an enterprise's customer",
      message_fr:
        "Vous venez d'accepter l'invitation. Vous devez vous reconnecter à l'application pour y accéder en tant que client d'une entreprise",
    });
  }

  if (data?.passwordChanged) {
    alertSuccess({
      message:
        "All sessions were signed out. Please sign in again with your new password.",
      message_fr:
        "Toutes les sessions ont été fermées. Veuillez vous reconnecter avec votre nouveau mot de passe.",
    });
  }
};

export const logout = (history, data) => {
  return async (dispatch) => {
    // The local teardown runs first and on every dispatch, before anything is
    // awaited. The old shape returned early while a request was in flight, leaving
    // the second caller holding a token, a socket and a store that all still said
    // signed in (F-26).
    await endSessionLocally(dispatch);

    if (!logoutInFlight) {
      // No body at all: BE-4 takes identity from the refresh cookie and nothing
      // else, which is what stopped anyone who knew a keycloak id from ending that
      // user's sessions (F-10). withCredentials belongs on this call and
      // /auth/refresh and nowhere else - on the shared instance it would force
      // every service to answer Allow-Credentials or the browser drops the
      // response, even on calls that send no cookie. Deliberately not
      // axiosInstance, for the reason the refresh call is not either: this one
      // authenticates with the cookie, and a failure here has nothing for the
      // response interceptor to refresh.
      logoutInFlight = axios
        .post(`${process.env.REACT_APP_API_URL}/auth/logout`, null, {
          withCredentials: true,
        })
        .finally(() => {
          logoutInFlight = null;
        });
    }

    try {
      await logoutInFlight;
    } catch (error) {
      // Every BE-4 response clears the cookie, the failures included, and the
      // local session is already gone. A keycloak session that outlives this one
      // is no longer reachable from this browser, so the user leaves either way.
      console.log({ error });
      dispatch(requestFailed(error?.response?.data));
    }

    redirectAfterLogout(history, data);
  };
};

// The sign-out modal's own way out, and deliberately not `logout`. BE-3 cleared
// the refresh cookie on the terminal response, so POSTing /auth/logout from here
// would be a cookieless no-op - a 401 charged to BE-5's address-keyed bucket,
// which a whole NAT shares. The token is already gone; the storage keys are
// deliberately not, because the tree behind the modal is still rendering from
// them, so clearing them is this call's job and has to happen here rather than
// in the interceptor. This finishes the storage, the socket and the store, and
// makes the redirect the interceptor withheld until the user had read the modal.
export const acknowledgeSessionEnded = (history) => {
  return async (dispatch) => {
    await endSessionLocally(dispatch);
    redirectAfterLogout(history);
  };
};

export const requestLogin = (
  googleLogin,
  email,
  password,
  redirectUrl,
  codeResponse,
  history,
  accountType,
  invitationUid
) => {
  return async (dispatch) => {
    dispatch(requestStarted());
    try {
      // withCredentials on the three login calls: this is the response that
      // carries Set-Cookie for the refresh cookie, and a cross-origin XHR made
      // without credentials mode has its Set-Cookie *discarded* by the browser.
      // /auth/refresh then correctly reports no cookie and the session ends at
      // the first token expiry.
      //
      // Same-origin preprod honours Set-Cookie either way, so this can only
      // fail where the front and the API are different hosts -- the same blind
      // spot that makes the CORS check production-only.
      //
      // Scoped to these calls rather than the shared instance, for the reason
      // given on /auth/refresh and /auth/logout: on the instance it would force
      // every service to answer Allow-Credentials, including those that send no
      // cookie at all.
      const credentialed = { withCredentials: true };
      const res = googleLogin
        ? invitationUid
          ? await axiosInstance.post(
              `/auth/login_google`,
              {
                code: codeResponse?.code,
                invitation_uid: invitationUid,
              },
              credentialed,
            )
          : await axiosInstance.post(
              `/auth/login_google`,
              {
                code: codeResponse?.code,
              },
              credentialed,
            )
        : await axiosInstance.post(
            `/auth/login`,
            { email, password },
            credentialed,
          );
      dispatch(requestSucceeded());

      if (res?.data?.google_register) {
        clearRiskAssessmentStorage();
        dispatch(setGoogleSignUp({ ...res?.data, accountType }));
        if (invitationUid) {
          history.push(`/register?invitation_uid=${invitationUid}`);
        } else {
          history.push("/register");
        }
        // The google refresh token in this branch still travels to /google_register
        // through the ssoUser slice setGoogleSignUp just filled, which is the one
        // place BE-2 deliberately left alone (F-36). It no longer reaches
        // localStorage: nothing read it from there but the logout that is gone.
        const { token } = res.data;
        setAccessToken(token);
      } else {
        clearRiskAssessmentStorage();
        const { token } = res.data;
        const { organization_id } = res.data.user;
        const { user } = res.data;
        setAccessToken(token);
        localStorage.setItem("orgaId", organization_id);
        dispatch(setOrganizationId(organization_id));
        localStorage.setItem("user", JSON.stringify(user));
        dispatch(login(token, organization_id, redirectUrl));
      }
    } catch (error) {
      console.log(error?.response?.data);

      dispatch(requestFailed(error?.response?.data));
    }
  };
};

export const requestRegister = (
  {
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    phoneNumber,
    country,
    enterprise,
  },
  checked,
  accountType,
  history
) => {
  return async (dispatch, getState) => {
    dispatch(requestStarted());
    try {
      const res = await axiosInstance.post(`/auth/register`, {
        first_name: firstName,
        last_name: lastName,
        email: email,
        country: country,
        password: password,
        confirm_password: confirmPassword,
        phone: phoneNumber,
        entreprise: enterprise,
        check: checked,
        type: accountType,
      });
      dispatch(requestSucceeded());
      alertSuccess(res.data);
      history.push("/login");
    } catch (error) {
      if (error?.response?.status === 300) {
        dispatch(setAuthDialog(true));
        dispatch(requestSucceeded());
      } else {
        console.log({ error });
        dispatch(requestFailed(error?.response?.data));
      }
    }
  };
};

export const requestGoogleRegister = (
  email,
  phoneNumber,
  country,
  enterprise,

  checked,
  accountType,
  providertoken,
  refreshToken,
  invitationUid,
  history
) => {
  return async (dispatch, getState) => {
    const headers = {
      "x-access-token": providertoken,
      "x-refresh-token": refreshToken,
    };
    dispatch(requestStarted());
    try {
      const res = invitationUid
        ? await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/google_register`,
            {
              email: email,
              phone: phoneNumber,
              country: country,
              entreprise: enterprise,

              check: checked,
              type: "free",
              verified_email: true,
              invitation_uid: invitationUid,
            },
            {
              headers,
            }
          )
        : await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/google_register`,
            {
              email: email,
              phone: phoneNumber,
              country: country,
              entreprise: enterprise,

              check: checked,
              type: accountType,
              verified_email: true,
            },
            {
              headers,
            }
          );
      dispatch(requestSucceeded());
      if (history?.push) {
        alertSuccess(res.data);
        clearRiskAssessmentStorage();
        setAccessToken(null);
        history.push("/login");
        return;
      }
      clearRiskAssessmentStorage();
      const { organization_id } = res.data.user;
      const { user } = res.data;
      const { token } = res.data;
      localStorage.setItem("orgaId", organization_id);
      dispatch(setOrganizationId(organization_id));
      localStorage.setItem("user", JSON.stringify(user));
      setAccessToken(token);
      dispatch(login(token, organization_id));
      dispatch(setGoogleSignUp({}));
    } catch (error) {
      console.log({ error });
      setAccessToken(null);
      dispatch(requestFailed(error?.response?.data));
    }
  };
};

export const confirmAccount = ({ code, public_id }, history) => {
  return async (dispatch) => {
    dispatch(requestStarted());
    try {
      const res = await axiosInstance.post(`/auth/confirm_email`, {
        code,
        public_id,
      });
      dispatch(requestSucceeded());
      alertSuccess(res.data);
      history.push("/login");
    } catch (error) {
      console.log({ error });
      dispatch(requestFailed(error?.response?.data));
    }
  };
};

export const forgotPassword = (email) => {
  return async (dispatch) => {
    dispatch(requestStarted());
    try {
      const res = await axiosInstance.post(`/auth/forgot_password`, { email });
      dispatch(requestSucceeded());
      alertSuccess(res.data);
      // history.push("/login");
    } catch (error) {
      console.log({ error });
      dispatch(requestFailed(error?.response?.data));
    }
  };
};

export const resetPassword = (token, password, confirm_password, history) => {
  return async (dispatch) => {
    dispatch(requestStarted());
    try {
      const res = await axiosInstance.post(`/auth/reset_password`, {
        token,
        password,
        confirm_password,
      });
      dispatch(requestSucceeded());
      alertSuccess(res.data);
      // The server ended every session the old password authenticated and cleared
      // the refresh cookie with them (F-43). Tearing the local one down here is
      // what ties the sign-out to the password change: left alone, this browser
      // keeps looking signed in until a refresh drops it minutes later on the
      // terminal body, with nothing connecting the two. No /auth/logout call - the
      // session it would end is already gone.
      await endSessionLocally(dispatch);
      redirectAfterLogout(history, { passwordChanged: true });
    } catch (error) {
      console.log({ error });
      dispatch(requestFailed(error?.response?.data));
    }
  };
};

export const requestCheckOrgaId = (email, orgaName, handleClose) => {
  return async (dispatch) => {
    dispatch(requestStarted());
    try {
      const res = await axiosInstance.post(`/model/check_profile`, {
        email: email,
        organization_name: orgaName,
      });
      dispatch(requestSucceeded());
      alertSuccess(res.data);
      const { organization_id } = res.data;
      localStorage.setItem("orgaId", organization_id);
      dispatch(setOrganizationId(organization_id));
      dispatch(checkOrgaProfile(organization_id));
      handleClose();
    } catch (error) {
      console.log({ error });
      dispatch(requestFailed(error?.response?.data));
    }
  };
};

export const requestUpdatingProfile = (
  first_name,
  last_name,
  avatarSeleceted
) => {
  return async (dispatch) => {
    dispatch(requestStarted());
    try {
      const res = await axiosInstance.put(`/auth/update`, {
        first_name: first_name,
        last_name: last_name,
        avatar_url: avatarSeleceted,
      });
      dispatch(fetchProfile());

      dispatch(requestSucceeded());
      alertSuccess(res.data);
    } catch (error) {
      console.log({ error });
      dispatch(requestFailed(error?.response?.data));
    }
  };
};

export const requestChangePassword = (
  oldPassword,
  newPassword,
  confirmPassword,
  history
) => {
  return async (dispatch) => {
    dispatch(requestStarted());
    try {
      const res = await axiosInstance.post(`/auth/modify_password`, {
        old_password: oldPassword,
        password: newPassword,
        confirm_password: confirmPassword,
      });
      dispatch(requestSucceeded());
      alertSuccess(res.data);
      // Same as /reset_password, and this is the path a signed-in user actually
      // takes: every session is gone and the cookie with it, so this browser signs
      // out on purpose rather than discovering it at the next refresh (F-43).
      await endSessionLocally(dispatch);
      redirectAfterLogout(history, { passwordChanged: true });
    } catch (error) {
      console.log({ error });
      dispatch(requestFailed(error?.response?.data));
    }
  };
};

export const fetchProfile = () => {
  return async (dispatch) => {
    dispatch(requestStarted());
    try {
      const res = await axiosInstance.get(`/auth/get_user`);
      const profile = res.data;
      const storedUserRaw = localStorage.getItem("user");
      let storedUser = {};
      if (storedUserRaw) {
        try {
          storedUser = JSON.parse(storedUserRaw);
        } catch (e) {
          storedUser = {};
        }
      }

      localStorage.setItem(
        "user",
        JSON.stringify({
          ...storedUser,
          role: profile?.role ?? storedUser?.role,
          account_type: profile?.account_type ?? storedUser?.account_type,
          avatar_url: profile?.avatar_url ?? storedUser?.avatar_url,
          first_name: profile?.first_name ?? storedUser?.first_name,
          last_name: profile?.last_name ?? storedUser?.last_name,
          email: profile?.email ?? storedUser?.email,
          credit_tier: profile?.credit_tier ?? storedUser?.credit_tier,
          credit_remaining: profile?.credit_remaining ?? storedUser?.credit_remaining,
          credit_reserved: profile?.credit_reserved ?? storedUser?.credit_reserved,
          credit_available: profile?.credit_available ?? storedUser?.credit_available,
          credit_tier_costs: profile?.credit_tier_costs ?? storedUser?.credit_tier_costs,
          credit_tiers: profile?.credit_tiers ?? storedUser?.credit_tiers,
          credit_contact_email:
            profile?.credit_contact_email ?? storedUser?.credit_contact_email,
        })
      );
      dispatch(getProfile(profile));
      dispatch(requestSucceeded());
    } catch (error) {
      console.log({ error });
      dispatch(requestFailed(error?.response?.data));
    }
  };
};
